import {FC, useCallback, useMemo, useRef, useState} from 'react';
import {Box, Flex, Tabs} from '@chakra-ui/react';
import {EllipseButton, Modal, useScreenSize} from 'ui-kit';
import {useT} from '@kcast-fe/core';

import {Memories, Notes, Reminders, Summaries} from './components';

const MAX_WIDTH = {base: '796px', smToMd: 'calc(100% - 12px)'};

export type TMainTab = 'summary' | 'memory' | 'notes' | 'reminders';
export type TMemoryView =
  | 'summary_list'
  | 'summary_create'
  | 'summary_view'
  | 'memory_list'
  | 'notes_list'
  | 'notes_create'
  | 'notes_edit'
  | 'reminders_list'
  | 'reminders_create'
  | 'reminders_edit';

const MemorySceneFC: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [mainTab, setMainTab] = useState<TMainTab>('summary');
  const [currentView, setCurrentView] = useState<TMemoryView>('summary_list');

  const {isLessThan768} = useScreenSize();

  const {t} = useT();

  const handleBack = useCallback(() => {
    if (currentView === 'summary_create' || currentView === 'summary_view') {
      setCurrentView('summary_list');
    } else if (currentView === 'notes_create' || currentView === 'notes_edit') {
      setCurrentView('notes_list');
    } else if (currentView === 'reminders_create' || currentView === 'reminders_edit') {
      setCurrentView('reminders_list');
    }
  }, [currentView]);

  const title = useMemo(() => {
    const backHeader = (label: string) => (
      <Flex alignItems={'center'} gap={'16px'}>
        <EllipseButton
          isRounded
          icon={'Back'}
          size={'normal'}
          iconSize={'s20'}
          variant={'light'}
          onClick={handleBack}
        />
        <Box color={'{colors.textPrimary}'}>{label}</Box>
      </Flex>
    );

    if (currentView === 'summary_create') {
      return backHeader(t('summary.newSummary'));
    }

    if (currentView === 'summary_view') {
      return backHeader(t('summary.title'));
    }

    if (currentView === 'notes_create') {
      return backHeader(t('notes.newNote'));
    }

    if (currentView === 'notes_edit') {
      return backHeader(t('notes.editNote'));
    }

    if (currentView === 'reminders_create') {
      return backHeader(t('reminders.newReminder'));
    }

    if (currentView === 'reminders_edit') {
      return backHeader(t('reminders.editReminder'));
    }

    return (
      <Tabs.Root value={mainTab} variant={'subtle'} size={isLessThan768 ? 'sm' : 'md'}>
        <Tabs.List>
          <Tabs.Trigger
            outline={'none'}
            p={isLessThan768 ? '8px' : '12px'}
            fontSize={isLessThan768 ? '12px' : '14px'}
            fontWeight={'semibold'}
            value={'summary'}
            color={'{colors.textPrimary}'}
            _selected={{bg: '{colors.bgMain}'}}
            onClick={() => {
              setMainTab('summary');
              setCurrentView('summary_list');
            }}
          >
            Summaries
          </Tabs.Trigger>
          <Tabs.Trigger
            outline={'none'}
            p={isLessThan768 ? '8px' : '12px'}
            fontSize={isLessThan768 ? '12px' : '14px'}
            fontWeight={'semibold'}
            value={'memory'}
            color={'{colors.textPrimary}'}
            _selected={{bg: '{colors.bgMain}'}}
            onClick={() => {
              setMainTab('memory');
              setCurrentView('memory_list');
            }}
          >
            Memories
          </Tabs.Trigger>
          <Tabs.Trigger
            outline={'none'}
            p={isLessThan768 ? '8px' : '12px'}
            fontSize={isLessThan768 ? '12px' : '14px'}
            fontWeight={'semibold'}
            value={'notes'}
            color={'{colors.textPrimary}'}
            _selected={{bg: '{colors.bgMain}'}}
            onClick={() => {
              setMainTab('notes');
              setCurrentView('notes_list');
            }}
          >
            Notes
          </Tabs.Trigger>
          <Tabs.Trigger
            outline={'none'}
            p={isLessThan768 ? '8px' : '12px'}
            fontSize={isLessThan768 ? '12px' : '14px'}
            fontWeight={'semibold'}
            value={'reminders'}
            color={'{colors.textPrimary}'}
            _selected={{bg: '{colors.bgMain}'}}
            onClick={() => {
              setMainTab('reminders');
              setCurrentView('reminders_list');
            }}
          >
            Reminders
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
    );
  }, [currentView, mainTab, isLessThan768, handleBack, t]);

  return (
    <Modal isOpen isFullHeight title={title} maxW={MAX_WIDTH}>
      <Box ref={ref} h={'calc(100% - 24px)'} overflow={'hidden'}>
        {mainTab === 'summary' && (
          <Summaries ref={ref} currentView={currentView} onSetCurrentView={setCurrentView} />
        )}
        {mainTab === 'memory' && <Memories ref={ref} currentView={currentView} />}
        {mainTab === 'notes' && (
          <Notes ref={ref} currentView={currentView} onSetCurrentView={setCurrentView} />
        )}
        {mainTab === 'reminders' && <Reminders ref={ref} />}
      </Box>
    </Modal>
  );
};

export const MemoryScene = MemorySceneFC;
